// The one-release migration out of `localStorage` (decision D11, ruling R-10).
//
// A browser that last ran a pre-cookie SDK holds a refresh token under `zion_refresh_token`. On
// the first boot after this release the token moves into the module variable below and the three
// keys go, before any network call (security chain C1 step 4). The tab then sends that token in
// the refresh body on every attempt until core-api answers a `CookieSessionResponse`, which is the
// moment the cookie exists and the key is not written again.
//
// This file holds the package's only `localStorage.setItem` of a `zion_` key. The census in
// `no-web-storage-census.test.ts` exempts it by path and asserts the exemption list holds this one
// entry.
/** The `localStorage` key the pre-cookie SDK wrote the access token under. Read once per boot, then deleted (D11). */
export const LEGACY_ACCESS_TOKEN_KEY = 'zion_access_token';
/** The `localStorage` key the pre-cookie SDK wrote the refresh token under; the one key this module writes back during a mixed roll (R-10). */
export const LEGACY_REFRESH_TOKEN_KEY = 'zion_refresh_token';
/** The `localStorage` key the pre-cookie SDK wrote the expiry under. Read once per boot, then deleted (D11). */
export const LEGACY_EXPIRES_AT_KEY = 'zion_token_expires_at';
/**
 * The refresh token this tab still owes the server in a request body, or `null` once the tab is on
 * the cookie arm. One variable, five entry points: `migrateLegacyStorage` fills it,
 * `getLegacyRefreshToken` reads it, `storeLegacyRefreshToken` replaces it,
 * `reloadLegacyRefreshToken` re-reads it from the key another tab may have rotated (R-47), and
 * `clearLegacyRefreshToken` empties it.
 */
let legacyRefreshToken = null;
/**
 * Move the refresh token a previous release left in `localStorage` into module memory, and leave
 * nothing behind in Web Storage. Called once per boot by `hydrateTokens`, before any network call
 * (chain C1 step 4). The token then lives for the document's lifetime, and `doRefresh` sends it in
 * the refresh body on every attempt until a `CookieSessionResponse` arrives, so a network failure,
 * a 5xx, a 429 or a rejected 200 costs a retry and not the session (R-10).
 * @returns nothing. A caller reads the migrated token with `getLegacyRefreshToken()`.
 *
 * Errors: none. It makes no network call and catches nothing, because the package is browser-only
 * and `localStorage` is always present where it runs. Side effects: the module variable takes the
 * stored value when it is a non-empty string, then `localStorage.removeItem` runs on all three
 * keys, unconditionally. A browser that never held a legacy session, and every boot after the
 * first, leaves the module variable `null`.
 *
 * A second call in the same document finds the key gone and leaves the module variable as it is.
 * Two `AuthProvider` trees in one document each call `hydrateTokens`, so the second call must not
 * discard the token the first one migrated (scenario S40).
 */
export function migrateLegacyStorage() {
    const stored = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
    if (stored !== null && stored !== '')
        legacyRefreshToken = stored;
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    localStorage.removeItem(LEGACY_EXPIRES_AT_KEY);
}
/**
 * The refresh token this tab still has to send in the body, or `null` when the tab is on the
 * cookie arm. `doRefresh` reads it inside the refresh lock rather than taking it as an argument,
 * so whichever refresh runs first carries it and a coalesced second caller cannot drop it (R-10,
 * finding A-4). Throws nothing, reads no storage.
 */
export function getLegacyRefreshToken() {
    return legacyRefreshToken;
}
/**
 * Re-read `LEGACY_REFRESH_TOKEN_KEY` into the module variable, or clear the variable when the key
 * is gone.
 *
 * ⛔ THE SECRET NEVER TRAVELS ON THE CHANNEL; THE LEGACY KEY IS THE SHARED STATE (R-47). During a
 * mixed roll two tabs can both be on the body arm. Tab A refreshes, an N-1 pod rotates the family
 * and hands the token back, and `storeLegacyRefreshToken` writes it to the key that both tabs
 * read. Tab A then posts `token-refreshed`, and tab B adopts the access token — but B's own
 * module variable still holds the token A rotated away. B's next refresh presents it, and a reuse
 * past the 60-second grace kills the family for every tab.
 *
 * The one caller is `startSessionSync`'s `token-refreshed` arm, before it writes the store. An
 * absent key means the sender received a `CookieSessionResponse`: the cookie carries the family
 * now, so this tab's body-arm token is spent and the variable goes with it.
 *
 * Errors: none. Side effects: the module variable, and one `localStorage.getItem`.
 */
export function reloadLegacyRefreshToken() {
    const stored = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
    legacyRefreshToken = stored === null || stored === '' ? null : stored;
}
/**
 * Keep `token` for the next attempt and write it to `LEGACY_REFRESH_TOKEN_KEY`. The one caller is
 * `adoptSession` on a `TokenPairResponse`: an N-1 core-api pod rotated the family and set no
 * cookie, so the rotated token is this tab's only credential and it has to survive a reload
 * (R-10).
 * @param token the `refreshToken` of the `TokenPairResponse` just parsed, a non-empty string by
 *   the schema.
 *
 * Errors: none. Side effects: the module variable, then one `localStorage.setItem`. That call is
 * the package's only write of a `zion_` key; scenario S36 exempts this file by path and asserts
 * the exemption list holds that one entry.
 */
export function storeLegacyRefreshToken(token) {
    legacyRefreshToken = token;
    localStorage.setItem(LEGACY_REFRESH_TOKEN_KEY, token);
}
/**
 * Forget the legacy token: the module variable and `LEGACY_REFRESH_TOKEN_KEY`. Callers:
 * `adoptSession` on the first `CookieSessionResponse`, which is the moment this tab is on the
 * cookie arm for good, and `clearTokens`, so a revoked or refused credential is not left readable.
 * Idempotent, throws nothing.
 */
export function clearLegacyRefreshToken() {
    legacyRefreshToken = null;
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
}
//# sourceMappingURL=legacy-storage.js.map